import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, AlertTriangle, RotateCw, Copy } from "lucide-react";

/**
 * EmailPanel — admin diagnostics for the transactional email provider (Brevo).
 *
 * Shows:
 *   - Sender + domain authentication state (from Brevo /senders/domains)
 *   - 7-day delivery stats (requests vs delivered — surfaces "accepted but not delivered")
 *   - Email log with a per-row "resend" button
 *   - DNS instructions when the domain isn't authenticated yet
 */
export const EmailPanel = () => {
  const { api } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [dns, setDns] = useState(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendingId, setResendingId] = useState("");
  const [lastError, setLastError] = useState("");

  const load = useCallback(() => {
    api.get("/admin/email-log").then(r => setData(r.data)).catch(() => {});
    api.get("/admin/email-dns").then(r => setDns(r.data)).catch(() => setDns(null));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const copyValue = async (value) => {
    try { await navigator.clipboard.writeText(value); toast.success("Copiado al portapapeles"); }
    catch { toast.error("No se pudo copiar"); }
  };

  const test = async (e) => {
    e.preventDefault();
    setBusy(true); setLastError("");
    try {
      const { data: res } = await api.post("/admin/email-test", null, { params: { to } });
      toast.success(`${t("email_test_ok")} · ${res.provider_id}`);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || "Error";
      setLastError(msg);
      toast.error(t("email_test_fail"));
      load();
    } finally { setBusy(false); }
  };

  const resend = async (entryId) => {
    setResendingId(entryId);
    try {
      const { data: res } = await api.post(`/admin/email-resend/${entryId}`);
      if (res.ok) toast.success(t("email_resent") || "Reenviado");
      else toast.error(t("email_test_fail") || "No se pudo reenviar");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setResendingId(""); }
  };

  const verified = data?.domain_verified;
  const domainStatus = data?.domain_status || null;
  const stats = data?.stats_7d || null;
  const senderDomain = (data?.sender || "").split("@")[1] || "meoscargo.com";
  // Brevo accepted the request but didn't deliver → classic unauthenticated-domain signature
  const silentFailure = stats && stats.requests > 0 && stats.delivered === 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 space-y-3" data-testid="email-panel">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
            <Mail className="w-3.5 h-3.5" /> {t("email_notifications")}
          </h3>
          <p className="text-[11px] text-slate-400 mt-0.5">{t("email_sender")}: <span className="font-mono">{data?.sender || "—"}</span></p>
        </div>
        <span
          data-testid="email-status"
          className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full flex items-center gap-1.5 ${verified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
        >
          {verified ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
          {verified ? t("email_domain_ok") : t("email_domain_pending")}
        </span>
      </div>

      {/* 7-day delivery stats — surfaces the "accepted but silently dropped" scenario */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="email-stats">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("email_requested") || "Pedidos"} · 7d</div>
            <div className="text-lg font-bold text-slate-900 tabular-nums">{stats.requests || 0}</div>
          </div>
          <div className={`rounded-lg border p-2 ${stats.delivered > 0 ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{t("email_delivered") || "Entregados"}</div>
            <div className={`text-lg font-bold tabular-nums ${stats.delivered > 0 ? "text-emerald-700" : "text-red-700"}`}>{stats.delivered || 0}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("email_bounced") || "Rebotados"}</div>
            <div className="text-lg font-bold text-amber-700 tabular-nums">{(stats.hardBounces || 0) + (stats.softBounces || 0)}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("email_errors") || "Errores"}</div>
            <div className="text-lg font-bold text-red-700 tabular-nums">{stats.errors || 0}</div>
          </div>
        </div>
      )}

      {silentFailure && (
        <div className="rounded-lg border-2 border-red-300 bg-red-50 p-3 text-xs text-red-900 space-y-1.5" data-testid="email-silent-failure">
          <p className="font-bold flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Brevo acepta los correos pero NO los entrega</p>
          <p>En los últimos 7 días: <b>{stats.requests}</b> peticiones aceptadas · <b>{stats.delivered}</b> realmente entregados. Es la firma clásica de un dominio sin autenticar (SPF/DKIM/DMARC).</p>
        </div>
      )}

      {!verified && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-2" data-testid="email-warning">
          <p className="font-semibold">¿Por qué no llegan los correos?</p>
          <p>Añade / verifica estos registros DNS en <b>Hostinger → hPanel → DNS zone editor</b> para <b>{senderDomain}</b>. Brevo nos dice en vivo cuáles ya detecta (<CheckCircle2 className="w-3 h-3 inline" />) y cuáles faltan (<AlertTriangle className="w-3 h-3 inline" />).</p>
          {dns && dns.records && dns.records.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] font-mono" data-testid="email-dns-table">
                <thead className="text-[9px] uppercase tracking-wider text-amber-700">
                  <tr>
                    <th className="p-1.5 text-start">Estado</th>
                    <th className="p-1.5 text-start">Tipo</th>
                    <th className="p-1.5 text-start">Host / Name</th>
                    <th className="p-1.5 text-start">Valor</th>
                    <th className="p-1.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-200">
                  {dns.records.map((r, i) => (
                    <tr key={i} data-testid={`email-dns-row-${i}`}>
                      <td className="p-1.5">
                        {r.ok
                          ? <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 className="w-3 h-3" /> OK</span>
                          : <span className="inline-flex items-center gap-1 text-red-700 font-semibold"><AlertTriangle className="w-3 h-3" /> Falta</span>}
                      </td>
                      <td className="p-1.5 font-bold">{r.type}</td>
                      <td className="p-1.5 break-all">{r.host_name || "@"}</td>
                      <td className="p-1.5 break-all max-w-[320px]">{r.value}</td>
                      <td className="p-1.5 text-end">
                        <button type="button" onClick={() => copyValue(r.value)} data-testid={`email-dns-copy-${i}`}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-300 hover:bg-amber-100 text-[10px] font-semibold">
                          <Copy className="w-3 h-3" /> Copiar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="pt-2 text-[11px]">
                Después de añadirlos, vuelve a <a className="underline font-semibold" href="https://app.brevo.com/senders/domain/list" target="_blank" rel="noreferrer">app.brevo.com → Senders → Domains</a> y pulsa <b>Authenticate</b>. En 5-15 minutos este panel se pondrá verde y los correos empezarán a llegar.
              </p>
              <button
                type="button"
                data-testid="email-reauth-btn"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const { data: res } = await api.post("/admin/email-authenticate");
                    if (res.ok) toast.success("Brevo verificó el dominio correctamente");
                    else toast.error(res.brevo?.message || "Brevo aún no puede autenticar — revisa que los 4 registros estén en DNS");
                    load();
                  } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
                  finally { setBusy(false); }
                }}
                className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-amber-700 hover:bg-amber-800 text-white text-[11px] font-semibold">
                <RotateCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Pedir verificación a Brevo ahora
              </button>
            </div>
          ) : (
            <ol className="list-decimal ms-4 space-y-1">
              <li>Entra en <a className="underline" href="https://app.brevo.com/senders/domain/list" target="_blank" rel="noreferrer">app.brevo.com → Senders → Domains</a> y añade <b>{senderDomain}</b>.</li>
              <li>Copia los registros DNS que te muestre Brevo y pégalos en Hostinger (hPanel → DNS zone editor).</li>
              <li>Vuelve a Brevo y pulsa "Authenticate".</li>
            </ol>
          )}
          {domainStatus && (
            <p className="pt-1 border-t border-amber-200 font-mono text-[10px]">
              Brevo responde para <b>{domainStatus.domain}</b>: authenticated={String(domainStatus.authenticated)} · verified={String(domainStatus.verified)}
            </p>
          )}
        </div>
      )}

      <form onSubmit={test} className="flex flex-col sm:flex-row gap-2">
        <input
          required type="email" value={to} onChange={(e) => setTo(e.target.value)}
          placeholder={t("email_test_placeholder")}
          data-testid="email-test-to"
          className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-emerald-600"
        />
        <button type="submit" disabled={busy} data-testid="email-test-btn" className="bg-slate-900 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center justify-center gap-2">
          <Send className="w-4 h-4" /> {t("email_send_test")}
        </button>
      </form>

      {lastError && (
        <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 font-mono break-words" data-testid="email-error">{lastError}</p>
      )}

      {(data?.rows || []).length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[640px]">
            <thead className="text-[10px] uppercase text-slate-400">
              <tr>
                <th className="p-2 text-start">{t("date")}</th>
                <th className="p-2 text-start">{t("email")}</th>
                <th className="p-2 text-start">{t("subject")}</th>
                <th className="p-2 text-start">{t("status")}</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.slice(0, 10).map(r => (
                <tr key={r.id} data-testid={`email-row-${r.id}`}>
                  <td className="p-2 tabular-nums text-slate-500">{new Date(r.at).toLocaleString()}</td>
                  <td className="p-2 font-mono">{r.to}</td>
                  <td className="p-2 truncate max-w-[180px]">{r.subject}</td>
                  <td className="p-2">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${r.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {r.status === "sent" ? t("email_sent") : t("email_failed")}
                    </span>
                    {r.error && <span className="ms-2 text-[10px] text-red-600">{r.error.slice(0, 90)}</span>}
                  </td>
                  <td className="p-2 text-end">
                    <button
                      type="button"
                      onClick={() => resend(r.id)}
                      disabled={!!resendingId}
                      data-testid={`email-resend-${r.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 hover:border-indigo-400 hover:text-indigo-700 text-[10px] font-semibold disabled:opacity-50"
                      title={t("email_resend") || "Reenviar"}
                    >
                      <RotateCw className={`w-3 h-3 ${resendingId === r.id ? "animate-spin" : ""}`} />
                      {resendingId === r.id ? "…" : (t("email_resend") || "Reenviar")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
